// src/components/grammar/GrammarProgressTracker.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import {
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Target,
  Award,
  BarChart3,
  RefreshCcw,
  Trash2,
  BookOpen,
  ChevronRight,
    Bookmark
} from 'lucide-react';
import grammarProgressService from '../../services/grammarProgressService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

const GrammarProgressTracker = ({currentContext = null,grammarData = null, level, onReviewMistakes, onSelectPillar, onReviewBookmark, initialTab = 'overview' , showTabs = true }) => {

  const { t } = useTranslation();
  const [progress, setProgress] = useState(null);
  const [mistakes, setMistakes] = useState([]);
  const [weakAreas, setWeakAreas] = useState([]);
  const [loading, setLoading] = useState(true);
 const [activeTab, setActiveTab] = useState(initialTab);
 const [groupedMistakes, setGroupedMistakes] = useState({
    currentContext: [],
    samePillar: {},
    otherPillars: {},
    total: 0
  });
  const [currentContextName, setCurrentContextName] = useState('');
   const user = auth.currentUser;
   useEffect(() => {
  const loadGroupedMistakes = async () => {
    if (!progress || !level || !user) return;

    try {
      const grouped = await grammarProgressService.getMistakesGrouped(
        user.uid,
        level,
        currentContext
      );

      setGroupedMistakes(grouped);

      // Set current context name for display
      if (currentContext?.categoryName) {
        setCurrentContextName(`${currentContext.pillarId} → ${currentContext.categoryName}`);
      } else if (currentContext?.practiceLevelName) {
        setCurrentContextName(`${currentContext.pillarId} → ${currentContext.practiceLevelName}`);
      } else if (currentContext?.pillarId) {
        setCurrentContextName(currentContext.pillarId);
      }

      console.log('📊 Grouped mistakes loaded:', grouped);
    } catch (error) {
      console.error('Error loading grouped mistakes:', error);
    }
  };

  loadGroupedMistakes();
}, [progress, level, currentContext, user]);
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Get current user


  // Load progress data
  useEffect(() => {
    const loadProgressData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Load overall progress
        const userProgress = await grammarProgressService.getUserProgress(user.uid, level);
        setProgress(userProgress);

        // Load mistakes
        const userMistakes = await grammarProgressService.getMistakes(user.uid, level);
        setMistakes(userMistakes);

        // Load weak areas
        const userWeakAreas = await grammarProgressService.getWeakAreas(user.uid, level);
        setWeakAreas(userWeakAreas);
      } catch (error) {
        console.error('Error loading progress data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProgressData();
  }, [user, level]);

  // Handle mistake review
  const handleReviewMistake = async (mistake) => {
    if (!user) return;

    await grammarProgressService.markMistakeReviewed(user.uid, level, mistake.id);
    if (onReviewMistakes) {
      onReviewMistakes(mistake);
    }
  };

  // Clear all mistakes
  const handleClearMistakes = async () => {
    if (!user) return;

    if (window.confirm(t('grammar.mistakes.confirmClear'))) {
      await grammarProgressService.clearMistakes(user.uid, level);
      setMistakes([]);
    }
  };

  // Calculate statistics
  const getStatistics = () => {
    if (!progress?.stats) return null;

    const stats = progress.stats;
    const accuracy = stats.completedExercises > 0
      ? Math.round((stats.correctAnswers / stats.completedExercises) * 100)
      : 0;

    return {
      totalExercises: stats.totalExercises || 0,
      completedExercises: stats.completedExercises || 0,
      correctAnswers: stats.correctAnswers || 0,
      incorrectAnswers: stats.incorrectAnswers || 0,
      accuracy: accuracy,
      timeSpent: Math.round((stats.totalTimeSpent || 0) / 60), // in minutes
      streakDays: stats.streakDays || 0,
      lastStudyDate: stats.lastStudyDate
    };
  };

  const stats = getStatistics();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-xl font-bold text-gray-800">
          {t('grammar.progress.title')}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('grammar.progress.currentLevel')}: {level}
        </p>
      </div>

      {/* Tab Navigation */}
        {showTabs && (
      <div className="flex border-b border-gray-200">
       {['overview', 'mistakes', 'bookmarks', 'statistics'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              flex-1 px-4 py-3 text-sm font-medium transition-colors
              ${activeTab === tab 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }
            `}
          >
            {t(`grammar.progress.tabs.${tab}`)}
          </button>
        ))}
      </div>
            )}

      {/* Tab Content */}
      <div className="p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Overall Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t('grammar.progress.overall')}
                </span>
                <span className="text-sm text-gray-600">
                  {stats?.completedExercises || 0} / {stats?.totalExercises || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, 
                      (stats?.completedExercises || 0) / 
                      Math.max(1, stats?.totalExercises || 0) * 100
                    )}%`
                  }}
                />
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-gray-600">{t('grammar.stats.correctAnswers')}</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {stats?.correctAnswers || 0}
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-gray-600">{t('grammar.progress.accuracy')}</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {stats?.accuracy || 0}%
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-gray-600">{t('grammar.progress.timeSpent')}</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {stats?.timeSpent || 0}m
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-orange-600" />
                  <span className="text-xs text-gray-600">{t('grammar.progress.streak')}</span>
                </div>
                <div className="text-2xl font-bold text-orange-600">
                  {stats?.streakDays || 0}
                </div>
              </div>
            </div>

            {/* Weak Areas */}
            {weakAreas.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  {t('grammar.stats.weakAreas')}
                </h4>
                <div className="space-y-2">
                  {weakAreas.map((area) => (
                    <button
                      key={area.pillarId}
                      onClick={() => onSelectPillar && onSelectPillar(area.pillarId)}
                      className="w-full p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-200 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-orange-700">
                            {area.mistakeCount}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-800">
                          {area.pillarId}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mistakes Tab */}
        {/* Mistakes Tab - Smart Hierarchical View */}
{activeTab === 'mistakes' && (
  <div className="space-y-4">
    {/* Mistakes Header */}
    <div className="flex items-center justify-between">
      <p className="text-sm text-gray-600">
        {t('grammar.mistakes.mistakeCount', { count: mistakes.length })}
      </p>
      {mistakes.length > 0 && (
        <button
          onClick={handleClearMistakes}
          className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
        >
          <Trash2 className="w-4 h-4" />
          {t('grammar.mistakes.clearMistakes')}
        </button>
      )}
    </div>

    {/* Group mistakes by context */}
    {mistakes.length > 0 ? (
      <div className="space-y-4">
        {/* PRIORITY 1: Current Context Mistakes */}
        {groupedMistakes?.currentContext?.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">🎯</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {t('grammar.mistakes.currentContext', { defaultValue: 'Current Section' })}
                </h3>
                <p className="text-xs text-gray-600">
                  {currentContextName || 'Your current practice area'}
                </p>
              </div>
              <div className="ml-auto">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {groupedMistakes.currentContext.length} {t('grammar.mistakes.label', { defaultValue: 'mistakes' })}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {groupedMistakes.currentContext.map((mistake) => (
                <MistakeCard
                  key={mistake.id}
                  mistake={mistake}
                 onReview={handleReviewMistake}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        {/* PRIORITY 2: Same Pillar - Other Categories/Levels */}
        {groupedMistakes?.samePillar && Object.keys(groupedMistakes.samePillar).length > 0 && (
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">📂</span>
              </div>
              <h3 className="font-semibold text-gray-900">
                {t('grammar.mistakes.samePillar', { defaultValue: 'Same Topic - Other Sections' })}
              </h3>
            </div>
            <div className="space-y-3">
              {Object.entries(groupedMistakes.samePillar).map(([key, group]) => (
                <CollapsibleMistakeGroup
                  key={key}
                  groupName={group.name}
                  mistakes={group.mistakes}
                  onReview={handleReviewMistake}
                  t={t}
                  color="orange"
                />
              ))}
            </div>
          </div>
        )}

        {/* PRIORITY 3: Other Pillars */}
        {groupedMistakes?.otherPillars && Object.keys(groupedMistakes.otherPillars).length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">📚</span>
              </div>
              <h3 className="font-semibold text-gray-900">
                {t('grammar.mistakes.otherPillars', { defaultValue: 'Other Topics' })}
              </h3>
            </div>
            <div className="space-y-3">
              {Object.entries(groupedMistakes.otherPillars).map(([pillarId, group]) => (
                <CollapsibleMistakeGroup
                  key={pillarId}
                  groupName={group.name}
                  mistakes={group.mistakes}
                  onReview={handleReviewMistake}
                  t={t}
                  color="gray"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    ) : (
      <div className="text-center py-12 bg-green-50 rounded-lg border border-green-200">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <p className="text-gray-600 font-medium">{t('grammar.mistakes.noMistakes')}</p>
        <p className="text-sm text-gray-500 mt-2">
          {t('grammar.mistakes.keepGoing', { defaultValue: 'Great job! Keep practicing.' })}
        </p>
      </div>
    )}
  </div>
)}
        {activeTab === 'bookmarks' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">{t('grammar.bookmarks.title')}</h4>
            {progress?.bookmarks?.length > 0 ? (
              <div className="space-y-3">
                {progress.bookmarks.map((bookmark) => (
                  <div key={bookmark.exerciseId} className="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Pillar: {bookmark.pillarId}
                      </p>
                      <p className="text-xs text-gray-500">
                        Exercise ID: {bookmark.exerciseId}
                      </p>
                    </div>
                    <button
                      onClick={() => onReviewBookmark && onReviewBookmark(bookmark)}
                      className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                    >
                      {t('grammar.bookmarks.review')}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">{t('grammar.bookmarks.noBookmarks')}</p>
              </div>
            )}
          </div>
        )}
        {/* Statistics Tab */}
        {activeTab === 'statistics' && (
          <div className="space-y-6">
            {/* Detailed Statistics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('grammar.stats.totalExercises')}</p>
                <p className="text-2xl font-bold text-gray-800">{stats?.totalExercises || 0}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('grammar.progress.exercisesCompleted')}</p>
                <p className="text-2xl font-bold text-gray-800">{stats?.completedExercises || 0}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('grammar.stats.correctAnswers')}</p>
                <p className="text-2xl font-bold text-green-600">{stats?.correctAnswers || 0}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('grammar.stats.incorrectAnswers')}</p>
                <p className="text-2xl font-bold text-red-600">{stats?.incorrectAnswers || 0}</p>
              </div>
            </div>

            {/* Performance Chart */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                {t('grammar.stats.performance')}
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-24">{t('grammar.progress.accuracy')}</span>
                  <div className="flex-1 bg-white rounded-full h-4">
                    <div
                      className="bg-gradient-to-r from-green-400 to-green-600 h-4 rounded-full"
                      style={{ width: `${stats?.accuracy || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-800">{stats?.accuracy || 0}%</span>
                </div>
              </div>
            </div>

            {/* Last Study Date */}
            {stats?.lastStudyDate && (
              <div className="text-center py-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('grammar.progress.lastSession')}</p>
                <p className="text-lg font-medium text-blue-600">
                  {new Date(stats.lastStudyDate.seconds * 1000).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
const MistakeCard = ({ mistake, onReview, t }) => {
  return (
    <div className="bg-white rounded-lg p-3 border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {mistake.exerciseId}
            </span>
            {!mistake.reviewed && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                {t('grammar.mistakes.new', { defaultValue: 'New' })}
              </span>
            )}
          </div>
          <div className="text-sm space-y-1">
            <div className="flex items-start gap-2">
              <span className="text-gray-500 font-medium min-w-[80px]">
                {t('grammar.exercise.yourAnswer', { defaultValue: 'Your answer' })}:
              </span>
              <span className="text-red-600 font-medium">{mistake.userAnswer}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-gray-500 font-medium min-w-[80px]">
                {t('grammar.exercise.correctAnswer', { defaultValue: 'Correct' })}:
              </span>
              <span className="text-green-600 font-medium">{mistake.correctAnswer}</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => onReview(mistake)}
          className="flex-shrink-0 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
        >
          {t('grammar.mistakes.review', { defaultValue: 'Review' })}
        </button>
      </div>
    </div>
  );
};

// Collapsible Mistake Group Component
const CollapsibleMistakeGroup = ({ groupName, mistakes, onReview, t, color = 'gray' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const colorClasses = {
    orange: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-300',
      hover: 'hover:bg-orange-200'
    },
    gray: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-300',
      hover: 'hover:bg-gray-200'
    }
  };

  const colors = colorClasses[color] || colorClasses.gray;

  return (
    <div className={`border ${colors.border} rounded-lg overflow-hidden`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full ${colors.bg} ${colors.hover} p-3 flex items-center justify-between transition-colors`}
      >
        <div className="flex items-center gap-2">
          <ChevronRight
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
          <span className={`font-medium ${colors.text}`}>{groupName}</span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
            {mistakes.length}
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="p-3 bg-white space-y-2">
          {mistakes.map((mistake) => (
            <MistakeCard
              key={mistake.id}
              mistake={mistake}
              onReview={onReview}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};
export default GrammarProgressTracker;